import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { PrintIcon, SaveIcon } from '../../../shared-components/svgs/svgs';
import ConfirmPrintPopup from '../../../shared-components/popup/confirm/confirm-print-popup';
import { formatMessage } from '../../../translations/format-message';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import DragItems from '../../../assets/Images/drag.svg';

interface TeplarPrintTableProps {
  operatorTeplarDetails: any[];
  isTimerRunning: boolean;
  isProcessStarted: boolean;
  printCount: number[];
  handleDecrementPrintCount: (index: number) => void;
  handleIncrementPrintCount: (index: number) => void;
  handleChangePrintCount: (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => void;
  stringToHex: (text: string) => string;
  onSaveTeplarOrder: (
    orderData: { Id: number; Order: number; IsPrinted: boolean }[]
  ) => void;
  onOrderChange?: (
    orderData: { Id: number; Order: number; IsPrinted: boolean }[]
  ) => void;
  teplarList: any[];
  setTeplarList: React.Dispatch<React.SetStateAction<any[]>>;
  saveClicked: boolean;
  setSaveClicked: React.Dispatch<React.SetStateAction<boolean>>;
}

const TeplarPrintTable: React.FC<TeplarPrintTableProps> = ({
  operatorTeplarDetails,
  isTimerRunning,
  isProcessStarted,
  printCount,
  handleDecrementPrintCount,
  handleIncrementPrintCount,
  handleChangePrintCount,
  stringToHex,
  onSaveTeplarOrder,
  onOrderChange,
  teplarList,
  setTeplarList,
  saveClicked,
  setSaveClicked,
}) => {
  const [confirmPrint, setConfirmPrint] = useState<{
    index: number;
    href: string;
  } | null>(null);

  // ✅ sync with props and initialize local printed state
  useEffect(() => {
    setTeplarList((prev) =>
      (operatorTeplarDetails || []).map((item) => {
        const existing = prev.find((x) => x.Id === item.Id);

        return {
          ...item,
          isPrinted:
            existing?.isPrinted ?? item?.isPrinted ?? item?.Printed ?? false,
        };
      })
    );
  }, [operatorTeplarDetails]);

  useEffect(() => {
    const orderData = teplarList.map((item, index) => ({
      Id: item.Id,
      Order: index + 1,
      IsPrinted: item.isPrinted || item.Printed,
    }));
    onOrderChange?.(orderData);
  }, [teplarList, onOrderChange]);

  const handlePrintAction = (item: any, index: number) => {
    const copies = printCount[index] ?? 1;
    const href = `didprint:${stringToHex(String(item?.Items ?? ''))};${item?.Format ?? ''};${copies}`;

    if (item.isPrinted || item.Printed) {
      setConfirmPrint({ index, href });
      return;
    }

    setTeplarList((prevItems) =>
      prevItems.map((prevItem, prevIndex) =>
        prevIndex === index ? { ...prevItem, isPrinted: true } : prevItem
      )
    );
    window.location.href = href;
  };

  const handleConfirmPrint = () => {
    if (!confirmPrint) return;

    setTeplarList((prevItems) =>
      prevItems.map((prevItem, prevIndex) =>
        prevIndex === confirmPrint.index
          ? { ...prevItem, isPrinted: true }
          : prevItem
      )
    );
    window.location.href = confirmPrint.href;
    setConfirmPrint(null);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const reordered = Array.from(teplarList);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);

    setTeplarList(reordered);
    onOrderChange?.(
      reordered.map((item, index) => ({
        Id: item.Id,
        Order: index + 1,
        IsPrinted: item.isPrinted,
      }))
    );
  };

  const handleSaveOrder = () => {
    const orderData = teplarList.map((item, index) => ({
      Id: item.Id,
      Order: index + 1,
      IsPrinted: item.isPrinted,
    }));
    onSaveTeplarOrder(orderData);
  };

  const layoutCount = Math.max(
    0,
    ...(teplarList ?? []).map((item) => Number(item?.Layout) || 0)
  );

  return (
    <div className="card border rounded tableDevices tableDesign">
      {teplarList.length > 0 ? (
        <div className="table-responsive">
          <div className="react-bootstrap-table">
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="teplar">
                {(provided) => (
                  <>
                    <table className="table table-hover table-bordered">
                      <thead>
                        <tr>
                          <th className="position-sticky top-0 start-0 z-3">
                            <Button
                              className="btn-sm"
                              type="button"
                              disabled={teplarList.length === 0}
                              onClick={() => {
                                setSaveClicked(true);
                                handleSaveOrder();
                              }}
                            >
                              <SaveIcon
                                width={16}
                                height={16}
                              />
                            </Button>
                          </th>
                          <th>{formatMessage('no')}</th>
                          <th>{formatMessage('copies')}</th>
                          {/* <th>{formatMessage('postion')}</th> */}
                          {Array.from({ length: layoutCount }).map(
                            (_, index) => (
                              <th key={index}>
                                {formatMessage('print_content')} {index + 1}
                              </th>
                            )
                          )}
                          <th>{formatMessage('template')}</th>
                        </tr>
                      </thead>

                      <tbody
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {teplarList.map((item, index) => (
                          <Draggable
                            key={item.Id ?? index}
                            draggableId={`row-${item.Id ?? index}`}
                            index={index}
                          >
                            {(provided) => (
                              <tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={
                                  item.isPrinted || item.Printed
                                    ? 'table-success'
                                    : ''
                                }
                              >
                                <td
                                  className="position-sticky top-0 start-0 z-3"
                                  {...provided.dragHandleProps}
                                >
                                  <img
                                    src={DragItems}
                                    alt=""
                                    className="drag mx-auto d-block"
                                  />{' '}
                                </td>
                                <td>{index + 1}</td>
                                <td>
                                  <div className="d-flex">
                                    <div
                                      className={`input-group operator ${
                                        isProcessStarted
                                          ? 'cursor-not-allowed'
                                          : ''
                                      }`}
                                    >
                                      <button
                                        className="btn btn-outline-light pause"
                                        type="button"
                                        onClick={() =>
                                          handleDecrementPrintCount(index)
                                        }
                                        disabled={!isTimerRunning}
                                      >
                                        –
                                      </button>

                                      <input
                                        type="number"
                                        className="form-control text-center"
                                        value={
                                          printCount[index] > 0
                                            ? printCount[index]
                                            : 1
                                        }
                                        onChange={(e) =>
                                          handleChangePrintCount(e, index)
                                        }
                                        min={1}
                                        disabled={!isTimerRunning}
                                      />

                                      <button
                                        className="btn btn-outline-light"
                                        type="button"
                                        onClick={() =>
                                          handleIncrementPrintCount(index)
                                        }
                                        disabled={!isTimerRunning}
                                      >
                                        +
                                      </button>
                                    </div>

                                    <Button
                                      className="btn btn-print ms-2"
                                      type="button"
                                      disabled={!isTimerRunning}
                                      onClick={() =>
                                        handlePrintAction(item, index)
                                      }
                                    >
                                      <PrintIcon />
                                    </Button>
                                    {/* {(item.isPrinted || item.Printed) && (
                                      <small className="text-success ms-2 align-self-center">
                                        {formatMessage('printed')}
                                      </small>
                                    )} */}
                                  </div>
                                </td>

                                {Array.from({ length: layoutCount }).map(
                                  (_, cellIndex) => {
                                    const parsedItems = String(
                                      item?.Items ?? ''
                                    ).split(',');
                                    const data = parsedItems[cellIndex];
                                    return (
                                      <td
                                        key={`${item.Id ?? index}-${cellIndex}`}
                                      >
                                        {data || '-'}
                                      </td>
                                    );
                                  }
                                )}
                                <td>{item.Format}</td>
                                {/* <td className="position-template">
                                  <span>{item.Position}</span>
                                </td> */}
                              </tr>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </tbody>
                    </table>
                    {confirmPrint && (
                      <ConfirmPrintPopup
                        title="confirm_print_again_title"
                        message="message_print_again_confirm"
                        isLoading={false}
                        modalSubmit={handleConfirmPrint}
                        modalClosed={() => setConfirmPrint(null)}
                      />
                    )}
                  </>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </div>
      ) : (
        <div className="empty-row d-flex">
          <span className="empty-message text-center w-100">
            {formatMessage('no_teplar_data_available')}
          </span>
        </div>
      )}
    </div>
  );
};

export default TeplarPrintTable;
